import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

const avatarColorOptions = [
  { key: "brand", className: "bg-gradient-to-br from-brand-500 to-brand-700" },
  { key: "ink", className: "bg-gradient-to-br from-ink-700 to-ink-900" },
  { key: "emerald", className: "bg-gradient-to-br from-emerald-500 to-emerald-700" },
  { key: "rose", className: "bg-gradient-to-br from-rose-500 to-rose-700" },
  { key: "amber", className: "bg-gradient-to-br from-amber-500 to-amber-700" },
  { key: "indigo", className: "bg-gradient-to-br from-indigo-500 to-indigo-700" },
  { key: "teal", className: "bg-gradient-to-br from-teal-500 to-teal-700" },
  { key: "fuchsia", className: "bg-gradient-to-br from-fuchsia-500 to-fuchsia-700" },
];

function colorClass(key: string | null | undefined) {
  return avatarColorOptions.find((c) => c.key === key)?.className || avatarColorOptions[0].className;
}

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ProfilePage() {
  const { user, profile, loading, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [initials, setInitials] = useState("");
  const [avatarColor, setAvatarColor] = useState<string>("brand");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setJobTitle(profile.job_title || "");
      setInitials(profile.initials || pickInitials(profile.full_name || ""));
      setAvatarColor(profile.avatar_color || "brand");
    }
  }, [profile]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const onSyncInitials = () => {
    setInitials(pickInitials(fullName));
  };

  const onSave = async () => {
    if (!user) return;
    setError("");
    setSaving(true);

    const cleanedInitials = (initials || pickInitials(fullName)).slice(0, 3).toUpperCase();

    const { error: upErr } = await supabase
      .from("profiles")
      .update({
        full_name: fullName.trim() || null,
        job_title: jobTitle.trim() || null,
        initials: cleanedInitials,
        avatar_color: avatarColor,
        updated_at: new Date().toISOString(),
      })
      .eq("id", user.id);

    if (upErr) {
      setError(upErr.message);
      setSaving(false);
      return;
    }

    await refreshProfile();
    setSaving(false);
    setToast("Profile updated");
  };

  const onSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const displayName = fullName || user?.email?.split("@")[0] || "Member";
  const previewInitials = (initials || pickInitials(fullName) || "??").toUpperCase();
  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs text-ink-500 mb-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="hover:text-ink-900 cursor-pointer"
          >
            Dashboard
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-ink-900 font-semibold">Your profile</span>
        </nav>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-ink-900">
              Your profile
            </h1>
            <p className="text-sm text-ink-500 mt-1">
              Update how you appear across the Haulmark portal.
            </p>
          </div>
          <button
            onClick={onSignOut}
            className="self-start inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold border border-ink-200 text-rose-600 hover:bg-rose-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i>
            Sign out
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-ink-100 rounded-lg p-10 text-center text-sm text-ink-500">
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Loading your profile…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left preview card */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-ink-100 rounded-lg p-6 flex flex-col items-center text-center">
                <div
                  className={`w-24 h-24 rounded-full text-white flex items-center justify-center text-2xl font-bold ${colorClass(
                    avatarColor,
                  )}`}
                >
                  {previewInitials}
                </div>
                <div className="mt-4 text-base font-bold text-ink-900 truncate max-w-full">
                  {displayName}
                </div>
                <div className="text-xs text-ink-500 mt-0.5 truncate max-w-full">{jobTitle || "Portal Member"}</div>
                <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cream-100 text-ink-700">
                  <i className="ri-shield-user-line"></i>
                  {profile?.role || "staff"}
                </span>

                <div className="w-full mt-6 pt-5 border-t border-ink-100 space-y-3 text-left">
                  <Row icon="ri-mail-line" label="Email" value={user?.email || "—"} mono />
                  <Row icon="ri-calendar-line" label="Member since" value={memberSince} />
                  <Row
                    icon="ri-fingerprint-line"
                    label="User ID"
                    value={user?.id ? `${user.id.slice(0, 8)}…` : "—"}
                    mono
                  />
                </div>
              </div>

              <div className="mt-3 bg-white border border-ink-100 rounded-lg p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                  Shortcuts
                </div>
                <ShortcutButton
                  icon="ri-dashboard-line"
                  label="Back to dashboard"
                  onClick={() => navigate("/dashboard")}
                />
                <ShortcutButton
                  icon="ri-briefcase-line"
                  label="My projects"
                  onClick={() => navigate("/projects")}
                />
                <ShortcutButton
                  icon="ri-team-line"
                  label="Crew roster"
                  onClick={() => navigate("/crew")}
                />
              </div>
            </div>

            {/* Right edit form */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-ink-100 rounded-lg p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="font-display text-lg font-bold text-ink-900">
                      Personal details
                    </h2>
                    <p className="text-xs text-ink-500 mt-0.5">
                      Visible to your teammates across the portal.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cream-100 text-ink-700">
                    <i className="ri-edit-line"></i>
                    Editable
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full name" required>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="e.g. Elena Rodriguez"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Job title">
                    <input
                      type="text"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g. Operations Manager"
                      className="form-input"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Initials">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={initials}
                        maxLength={3}
                        onChange={(e) => setInitials(e.target.value.toUpperCase())}
                        placeholder="ER"
                        className="form-input uppercase"
                      />
                      <button
                        type="button"
                        onClick={onSyncInitials}
                        className="shrink-0 inline-flex items-center gap-1 px-3 py-2.5 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-magic-line"></i>
                        Sync
                      </button>
                    </div>
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={user?.email || ""}
                      disabled
                      className="form-input bg-cream-100 text-ink-500 cursor-not-allowed"
                    />
                    <div className="text-[11px] text-ink-400 mt-1">
                      Email is managed by your account login.
                    </div>
                  </Field>
                </div>

                <div className="mt-5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                    Avatar color
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    {avatarColorOptions.map((opt) => {
                      const active = avatarColor === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setAvatarColor(opt.key)}
                          className={`w-10 h-10 rounded-full ${opt.className} flex items-center justify-center text-white text-xs font-bold border-2 cursor-pointer transition-transform ${
                            active ? "border-ink-900 scale-110" : "border-transparent"
                          }`}
                          aria-label={opt.key}
                        >
                          {active && <i className="ri-check-line"></i>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {error && (
                  <div className="mt-4 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{error}</span>
                  </div>
                )}

                <div className="mt-6 pt-5 border-t border-ink-100 flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="px-4 py-2 rounded-md text-sm font-semibold text-ink-500 hover:text-ink-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onSave}
                    disabled={saving || !fullName.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line"></i>
                        Save changes
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-white border border-ink-100 rounded-lg p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-lg font-bold text-ink-900">Account</h2>
                    <p className="text-xs text-ink-500 mt-0.5">
                      Sensitive options for your sign-in.
                    </p>
                  </div>
                </div>

                <div className="mt-4 divide-y divide-ink-100">
                  <AccountRow
                    icon="ri-lock-password-line"
                    title="Password"
                    desc="Use the Sign-in page to reset your password via email."
                    actionLabel="Reset"
                    onAction={() => navigate("/login")}
                  />
                  <AccountRow
                    icon="ri-shield-keyhole-line"
                    title="Role & permissions"
                    desc={`You are signed in as ${profile?.role || "staff"}. Contact an admin to change roles.`}
                    actionLabel="Learn more"
                    onAction={() => navigate("/dashboard")}
                  />
                  <AccountRow
                    icon="ri-logout-box-r-line"
                    title="Sign out"
                    desc="End this session on this device."
                    actionLabel="Sign out"
                    danger
                    onAction={onSignOut}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-emerald-400"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}

function Row({
  icon,
  label,
  value,
  mono,
}: {
  icon: string;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-cream-100 text-ink-500 shrink-0">
        <i className={`${icon} text-sm`}></i>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400">{label}</div>
        <div
          className={`text-xs text-ink-900 truncate ${mono ? "font-mono" : "font-semibold"}`}
          title={value}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function ShortcutButton({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-cream-50 text-sm text-ink-700 cursor-pointer text-left"
    >
      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-cream-100 text-ink-700">
        <i className={`${icon} text-sm`}></i>
      </span>
      <span className="font-semibold flex-1">{label}</span>
      <i className="ri-arrow-right-s-line text-ink-400"></i>
    </button>
  );
}

function AccountRow({
  icon,
  title,
  desc,
  actionLabel,
  onAction,
  danger,
}: {
  icon: string;
  title: string;
  desc: string;
  actionLabel: string;
  onAction: () => void;
  danger?: boolean;
}) {
  return (
    <div className="py-3.5 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-cream-100 text-ink-700 shrink-0">
          <i className={`${icon} text-base`}></i>
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink-900">{title}</div>
          <div className="text-xs text-ink-500 mt-0.5">{desc}</div>
        </div>
      </div>
      <button
        onClick={onAction}
        className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md border cursor-pointer whitespace-nowrap ${
          danger
            ? "border-rose-200 text-rose-600 hover:bg-rose-50"
            : "border-ink-200 text-ink-700 hover:bg-cream-100"
        }`}
      >
        {actionLabel}
      </button>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}